import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import axios from 'axios';
import BottomNavigation from '../components/BottomNavigation';
import EventShareControl from '../components/EventShareControl';
import InstallAppMenuItem from '../components/InstallAppMenuItem';
import { rememberDeepLink } from '../utils/deepLink';
import {
  ArrowLeft, Clock, Percent, Phone, MessageCircle,
  Globe, Building2, ChevronRight, Tag as TagIcon,
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;

const OfferDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [offer, setOffer] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    rememberDeepLink(`/offers/${id}`);
    axios.get(`${API}/api/offers/${id}`)
      .then((res) => setOffer(res.data))
      .catch(() => setNotFound(true))
      .finally(() => setLoading(false));
    axios.post(`${API}/api/posts/${id}/click`, {}).catch(() => {});
  }, [id]);

  const shareUrl = `${typeof window !== 'undefined' ? window.location.origin : 'https://find-2-day.com'}/api/share/offers/${id}`;
  const biz = offer?.business || {};

  const fmtDate = (d) => new Date(d).toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
  const daysLeft = (dateStr) => {
    const d = Math.ceil((new Date(dateStr) - new Date()) / (1000 * 60 * 60 * 24));
    return d > 0 ? d : 0;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center" data-testid="offer-detail-loading">
        <div className="animate-pulse text-gray-400">{t('loading', 'Loading…')}</div>
      </div>
    );
  }

  if (notFound || !offer) {
    return (
      <div className="min-h-screen bg-gray-50 flex flex-col items-center justify-center px-6 text-center" data-testid="offer-detail-notfound">
        <TagIcon className="h-12 w-12 text-gray-300 mb-3" />
        <h1 className="text-lg font-semibold text-gray-800">{t('offerDetail.notFoundTitle', 'Offer not available')}</h1>
        <p className="text-sm text-gray-500 mt-1 max-w-xs">{t('offerDetail.notFoundBody', 'This offer may have expired or been removed.')}</p>
        <button onClick={() => navigate('/offers')} className="mt-5 px-4 py-2 rounded-lg bg-orange-600 text-white text-sm font-medium hover:bg-orange-700" data-testid="offer-detail-browse-btn">
          {t('offerDetail.browseOffers', 'Browse offers')}
        </button>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50 pb-24" data-testid="offer-detail-page">
      {/* Header */}
      <div className="sticky top-0 z-20 bg-white/90 backdrop-blur border-b border-gray-100">
        <div className="max-w-2xl mx-auto px-4 h-14 flex items-center justify-between">
          <button onClick={() => navigate(-1)} className="inline-flex items-center gap-1 text-gray-600 hover:text-gray-900 text-sm" data-testid="offer-detail-back">
            <ArrowLeft className="h-4 w-4" /> {t('back', 'Back')}
          </button>
          <EventShareControl
            url={shareUrl}
            title={`${offer.title} – ${biz.businessName || ''}`.trim()}
            text={offer.description || t('shareDefaultText', 'Check out this on Find-2-Day')}
            testId={`offer-detail-${offer.id}`}
          />
        </div>
      </div>

      <div className="max-w-2xl mx-auto px-4">
        {/* Cover image */}
        {offer.image && (
          <div className="mt-4 rounded-2xl overflow-hidden bg-gray-100">
            <img src={offer.image} alt={offer.title} className="w-full max-h-80 object-cover" data-testid="offer-detail-image" />
          </div>
        )}

        {/* Discount + title */}
        <div className="mt-4 flex items-start gap-3">
          {offer.discountPercentage ? (
            <div className="w-16 h-16 bg-orange-100 rounded-xl flex flex-col items-center justify-center flex-shrink-0" data-testid="offer-detail-discount">
              <span className="text-xl font-bold text-orange-600 leading-none">{offer.discountPercentage}%</span>
              <span className="text-[10px] text-orange-500 font-medium">OFF</span>
            </div>
          ) : (
            <div className="w-16 h-16 bg-orange-100 rounded-xl flex items-center justify-center flex-shrink-0">
              <Percent className="h-7 w-7 text-orange-500" />
            </div>
          )}
          <div className="min-w-0">
            {offer.boosted && (
              <span className="inline-flex items-center px-2 py-0.5 rounded-full bg-gradient-to-r from-amber-400 to-orange-400 text-white text-[10px] font-bold mb-1" data-testid="offer-detail-sponsored">
                {t('sponsored', 'Sponsored')}
              </span>
            )}
            <h1 className="text-2xl font-bold text-gray-900" data-testid="offer-detail-title">{offer.title}</h1>
          </div>
        </div>

        {/* Validity */}
        {offer.validUntil && (
          <div className="mt-4 bg-white rounded-xl border border-gray-100 shadow-sm">
            <div className="flex items-center gap-3 p-4 text-sm" data-testid="offer-detail-validity">
              <Clock className="h-5 w-5 text-orange-500 shrink-0" />
              <span className="text-gray-800">
                {daysLeft(offer.validUntil) === 0
                  ? t('expirestoday', 'Expires today')
                  : `${t('offerDetail.validUntil', 'Valid until')} ${fmtDate(offer.validUntil)} · ${daysLeft(offer.validUntil)} ${t('daysLeft', 'days left')}`}
              </span>
            </div>
          </div>
        )}

        {/* Description */}
        {offer.description && (
          <div className="mt-5" data-testid="offer-detail-description">
            <h2 className="text-base font-semibold text-gray-900 mb-1">{t('offerDetail.about', 'About this offer')}</h2>
            <p className="text-sm text-gray-700 whitespace-pre-wrap leading-relaxed">{offer.description}</p>
          </div>
        )}

        {/* Business info */}
        {biz.id && (
          <div className="mt-6">
            <h2 className="text-base font-semibold text-gray-900 mb-2">{t('offerDetail.offeredBy', 'Offered by')}</h2>
            <button
              onClick={() => navigate(`/business/${biz.id}#offer-${offer.id}`)}
              className="w-full flex items-center gap-3 p-3 bg-white rounded-xl border border-gray-100 shadow-sm hover:border-orange-200 text-left"
              data-testid="offer-detail-business"
            >
              {biz.logoUrl ? (
                <img src={biz.logoUrl} alt={biz.businessName} className="h-12 w-12 rounded-lg object-cover border border-gray-100" />
              ) : (
                <div className="h-12 w-12 rounded-lg bg-orange-100 flex items-center justify-center"><Building2 className="h-6 w-6 text-orange-500" /></div>
              )}
              <div className="flex-1 min-w-0">
                <p className="font-medium text-gray-900 truncate">{biz.businessName}</p>
                <p className="text-xs text-gray-500 truncate">{[biz.category, biz.city].filter(Boolean).join(' · ')}</p>
              </div>
              <ChevronRight className="h-5 w-5 text-gray-300" />
            </button>

            {/* Contact shortcuts */}
            <div className="flex flex-wrap gap-2 mt-2">
              {biz.phone && (
                <a href={`tel:${biz.phone}`} className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50" data-testid="offer-detail-phone">
                  <Phone className="h-4 w-4" /> {t('call', 'Call')}
                </a>
              )}
              {biz.whatsapp && (
                <a href={`https://wa.me/${biz.whatsapp.replace(/[^0-9]/g, '')}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50" data-testid="offer-detail-whatsapp">
                  <MessageCircle className="h-4 w-4" /> WhatsApp
                </a>
              )}
              {biz.website && (
                <a href={biz.website.startsWith('http') ? biz.website : `https://${biz.website}`} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg border border-gray-200 text-sm text-gray-700 hover:bg-gray-50" data-testid="offer-detail-website">
                  <Globe className="h-4 w-4" /> {t('website', 'Website')}
                </a>
              )}
            </div>
          </div>
        )}

        {/* Install app prompt (hidden if already installed) */}
        <div className="mt-6">
          <InstallAppMenuItem surface="offer_detail" variant="card" testId="offer-detail-install" />
        </div>
      </div>

      <BottomNavigation />
    </div>
  );
};

export default OfferDetail;
